/**
 * @packageDocumentation
 * The Iframe SDK is intended for scenarios where developers wish to implement Myop components as separate web applications hosted within iframes.<br/><br/>
 * By importing the Iframe SDK, developers gain access to tools and utilities necessary for handling communication between the host application and the embedded iframe-based Myop components<br/><br/>
 * This SDK facilitates secure isolation, allowing components to function independently while still benefiting from the overall structure and management capabilities provided by the host application.<br/><br/>
 *
 Once loaded the `RootSDK`, you can load the `IframeSDK`
 ```js
 const {IframeSDK} = (await window.myop.rootSDK.getIframeModule());
 ```
 or
 ```ts
 import {getIframeModule} from '@myop/sdk';
 const {IframeSDK} = (await getIframeModule());
 ```
 or
 ```ts
 import {IframeSDK} from '@myop/sdk/iframe';
 ```
 *
 */
import {BaseMyopSDK} from "../common";
import {DetectMyopRefsMessageHandler} from "./messageHandlers/DetectMyopRefsMessageHandler";
import {BaseMessageHandler} from "../common";
import {BindOnClickHandler} from "./messageHandlers/bindOnClickHandler";
import {ChangeTextHandler} from "./messageHandlers/changeTextHandler";
import {ExecuteScriptHandler} from "./messageHandlers/executeScriptHandler";
import {defineDynamicImport} from "../dynamicImport/defineDynamicImport";
import {AddEventListenerHandler} from "./messageHandlers/addEventListenerHandler";
import {setAttributeHandler} from "./messageHandlers/setAttributeHandler";
import {
    InitRequest,
    MessageDirection,
    MyopBindMessage,
    MyopBindReplayMessage
} from "../messages";
import {CreateRefComponentMessageHandler} from "./messageHandlers/CreateRefComponentMessageHandler";
import {SetMutationObserverHandler} from "./messageHandlers/SetMutationObserverHandler";
import {GetElementValueHandler} from "./messageHandlers/GetElementValueHandler.ts";
// @ts-ignore handled by custom esbuild plugin
import version from "myop-sdk-version";
import {GetAttributeHandler} from "./messageHandlers/getAttributeHandler.ts";
import {SetResizeObserverHandler} from "./messageHandlers/SetResizeObserverHandler.ts";
import {SetInnerHtmlHandler} from "./messageHandlers/setInnerHtmlHandler.ts";

/**
 The Iframe SDK is intended for scenarios where developers wish to implement Myop components as separate web applications hosted within iframes.<br/><br/>
 By importing the Iframe SDK, developers gain access to tools and utilities necessary for handling communication between the host application and the embedded iframe-based Myop components<br/><br/>
 This SDK facilitates secure isolation, allowing components to function independently while still benefiting from the overall structure and management capabilities provided by the host application.<br/><br/>

 Once loaded the `RootSDK`, you can load the `IframeSDK`
 ```js
 const {IframeSDK} = (await window.myop.rootSDK.getIframeModule());
 ```
 or
 ```ts
 import {getIframeModule} from '@myop/sdk';
 const {IframeSDK} = (await getIframeModule());
 ```
 */
export class IframeSDK extends BaseMyopSDK {

    public version = version;

    constructor() {
        super();
        // @ts-ignore
        if (!window.myop) {
            // @ts-ignore
            window.myop = {}
        }
        // @ts-ignore
        window.myop.IframeSDK = this;
    }

    messageToHost = (message: MyopBindReplayMessage, replayTo?: MyopBindMessage) => {
        if (!message.replayToHandler && replayTo?.handlerUniqueId)
            message.replayToHandler = replayTo.handlerUniqueId

        const componentIds = Object.keys(this.components);

        if (!message.destination) {
            if (replayTo?.route) {
                // taking the first component that initiated that cycle
                message.destination = replayTo.route[replayTo.route.length - 1];
            } else {
                message.destination = componentIds[0]
            }
        }
        if (!message.source)
            message.source = componentIds[0];

        message.direction = MessageDirection.up

        window.parent?.postMessage({
            ...message
        }, '*');
    }

    supportedHandlers: BaseMessageHandler[] = [
        new ChangeTextHandler(this.messageToHost),
        new SetInnerHtmlHandler(this.messageToHost),
        new BindOnClickHandler(this.messageToHost),
        new DetectMyopRefsMessageHandler(this.messageToHost),
        new ExecuteScriptHandler(this.messageToHost),
        new AddEventListenerHandler(this.messageToHost),
        new setAttributeHandler(this.messageToHost),
        new GetElementValueHandler(this.messageToHost),
        new SetMutationObserverHandler(this.messageToHost),
        new SetResizeObserverHandler(this.messageToHost),
        new GetAttributeHandler(this.messageToHost),

        //composite support messages :
        new CreateRefComponentMessageHandler(this.messageToHost)
    ];

    public init = () => {
        window.onmessage = this.handleIncomeMessages.bind(this)
        this.messageToHost(new InitRequest())
    }

}

defineDynamicImport('IframeSDK', IframeSDK);
